import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { TablePaginationConfig } from "antd/lib/table/interface";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ButtonType } from "antd/lib/button";
import TableFilter from "../TableFilter";
import Table, { TableProps } from "../../atoms/Table";
import {
  ButtonWrap,
  TableHeader,
  TextPlace,
  TableBody,
  PaginationTableContainer,
} from "./styles";
import { TableFilterForm } from "../TableFilter/interfaces";
import Button from "../../atoms/Button";
import PageSelect from "./PageSelect";
import PageTitle from "../../atoms/PageTitle";

interface HeaderButtonType {
  type: ButtonType;
  onClick: () => void;
  isGhost?: boolean;
  label: string;
}
interface PaginationTableProps extends TableProps {
  headerTitle?: string;
  pageTitle?: string;
  initialFilterValues?: Record<string, any>;
  filterForms?: TableFilterForm[];
  totalCount?: number;

  headerButtonList?: HeaderButtonType[];
  fetchAction?: (
    page: number,
    pageSize: number,
    filterValues: any
  ) => Promise<any>;
  ref?: React.Ref<void>;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
const QUERY_KEY_FILTER = "filter";
const QUERY_KEY_PAGE = "page";
const QUERY_KEY_PAGE_SIZE = "pageSize";

const PaginationFilterTable = forwardRef<
  { reload: () => void; reset: () => void },
  PaginationTableProps
>((props: PaginationTableProps, ref) => {
  const getFilterValuesFromQuery = (): Record<string, any> => {
    const filterInQuery = searchParams.get(QUERY_KEY_FILTER);
    try {
      if (filterInQuery) {
        return JSON.parse(filterInQuery);
      }
    } catch (e) {
      return initialFilterValues;
    }
    return initialFilterValues;
  };
  useImperativeHandle(ref, () => ({
    reload(value?: any) {
      if (fetchAction) pushPage(page, pageSize, { ...filterValues, ...value });
    },
    reset() {
      if (fetchAction) pushPage(1, pageSize, filterValues);
    },
  }));
  const getPaginationInfoFromQuery = () => {
    try {
      const pageInQuery = searchParams.get(QUERY_KEY_PAGE) || `${DEFAULT_PAGE}`;
      const pageSizeInQuery =
        searchParams.get(QUERY_KEY_PAGE_SIZE) || `${DEFAULT_PAGE_SIZE}`;
      const page = parseInt(pageInQuery, 10);
      const pageSize = parseInt(pageSizeInQuery, 10);
      return {
        page,
        pageSize,
      };
    } catch (e) {
      return {
        page: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    }
  };

  const {
    filterForms = [],
    initialFilterValues = {},
    fetchAction,
    title,
    totalCount = 0,
    headerTitle,
    pageTitle,
    headerButtonList,
    ...rest
  } = props;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUnFold, setIsUnfold] = useState(true);

  const { page, pageSize } = getPaginationInfoFromQuery();
  const filterValues = getFilterValuesFromQuery();
  const [tableLoading, setTableLoading] = useState(false);
  useEffect(() => {
    if (fetchAction) {
      (async function fetchData() {
        setTableLoading(true);
        try {
          await fetchAction(page, pageSize, filterValues);
        } catch (e) {
          // Do nothing
        } finally {
          setTableLoading(false);
        }
      })();
    }
  }, [location]);

  const pushPage = (page: number, pageSize: number, filterValues = {}) => {
    const { pathname } = location;
    const filterString = JSON.stringify(filterValues);

    const queryString = new URLSearchParams({
      [QUERY_KEY_PAGE]: `${page}`,
      [QUERY_KEY_PAGE_SIZE]: `${pageSize}`,
      [QUERY_KEY_FILTER]: filterString,
    }).toString();
    const nextUrl = `${pathname}?${queryString}`;
    navigate(nextUrl);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    pushPage(1, nextPageSize, filterValues);
  };

  const handlePaginationChange = (pagination: TablePaginationConfig) => {
    const { current = DEFAULT_PAGE } = pagination;
    pushPage(current, pageSize, filterValues);
  };

  return (
    <MainFlex>
      <MainWrap>
        {/* <PageTitleWrapper>
          <PageTitle>{pageTitle}</PageTitle>
        </PageTitleWrapper> */}
        {filterForms.length > 0 && (
          <TableFilter
            isUnFold={isUnFold}
            setIsUnFold={setIsUnfold}
            filterForms={filterForms}
            initialFilterValues={initialFilterValues}
            filterValues={filterValues}
            onSearchSubmit={(filterValues) => {
              pushPage(1, pageSize, filterValues);
            }}
            onSearchReset={() => {
              pushPage(1, 10, {});
            }}
            style={{
              marginBottom: "24px",
              maxWidth: "calc(100%)",
            }}
          />
        )}
        <PaginationTableContainer>
          <TableHeader>
            <TextPlace>{headerTitle}</TextPlace>
            <ButtonWrap>
              <div
                style={{
                  marginRight: "24px",
                  width: "135px",
                  display: "flex",
                  flexWrap: "nowrap",
                  lineHeight: "32px",
                }}
              >
                <PageSelect
                  value={pageSize}
                  onPageSizeChanged={handlePageSizeChange}
                />
                &nbsp; 개씩 보기
              </div>
              {headerButtonList && (
                <HeaderButtonWrap>
                  {headerButtonList.map((data) => (
                    <Button
                      key={data.label}
                      type={data.type}
                      onClick={data.onClick}
                      ghost={data.isGhost}
                    >
                      {data.label}
                    </Button>
                  ))}
                </HeaderButtonWrap>
              )}
            </ButtonWrap>
          </TableHeader>
          <TableBody>
            <StyledTable
              loading={tableLoading}
              size="large"
              pagination={{
                total: totalCount,
                showSizeChanger: false,
                current: page,
                pageSize,
                size: "default",
                style: { marginRight: "24px" },
              }}
              onChange={handlePaginationChange}
              scroll={{ ...rest.scroll, x: "1080px" }}
              {...rest}
            />
          </TableBody>
        </PaginationTableContainer>
        {rest.pagination !== false && totalCount > 0 && (
          <TotalCount>
            총 <TotalNumber>{totalCount}</TotalNumber>{" "}
          </TotalCount>
        )}
      </MainWrap>
    </MainFlex>
  );
});

export default PaginationFilterTable;

const MainWrap = styled.div`
  position: relative;
  width: 100%;
  /* margin-top: 64px; */
`;
const MainFlex = styled.div`
  display: flex;
  justify-content: flex-start;
`;
const TotalCount = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  position: absolute;
  bottom: 44px;
  left: 48px;
`;
const StyledTable = styled(Table)`
  .ant-table-row:hover {
    cursor: pointer;
  }
  .ant-table-tbody > tr.ant-table-row > tr {
    height: 56px;
  }
  .ant-table-tbody {
    height: 56px;
  }
  .ant-table-cell {
    height: 56px;
  }
  thead > tr > th.ant-table-cell {
    height: 48px;
  }
  .ant-table-ping-right .ant-table-cell-fix-right-first::after {
    box-shadow: none;
  }
`;
const TotalNumber = styled.span`
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  margin-left: 8px;
`;
const HeaderButtonWrap = styled.div`
  display: flex;
  gap: 0 12px;
`;
const PageTitleWrapper = styled.div`
  margin-bottom: 40px;
`;
